// System Settings Controller (Frontend)
// File: controllers/systemSettingsController.js

class SystemSettingsController {
    constructor() {
      this.STORAGE_KEY = 'system_settings';
      this.initializeDefaults();
    }
  
    /**
     * Initialize default settings
     */
    initializeDefaults() {
      const existing = localStorage.getItem(this.STORAGE_KEY);
      if (!existing) {
        const defaults = {
          appName: 'TRACKLIO HUB',
          appVersion: '1.0.0',
          maintenanceMode: false,
          maintenanceMessage: 'System is under maintenance. Please check back later.',
          
          emailSettings: {
            smtpHost: 'smtp.gmail.com',
            smtpPort: 587,
            smtpUser: '',
            smtpPassword: '',
            fromEmail: '',
            fromName: 'TRACKLIO HUB',
            enableSSL: true
          },
          
          smsSettings: {
            provider: 'none',
            apiKey: '',
            apiSecret: '',
            senderId: '',
            enabled: false
          },
          
          storageSettings: {
            provider: 'local',
            maxFileSize: 10485760,
            allowedFileTypes: ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx', 'xls', 'xlsx'],
            bucketName: '',
            region: '',
            accessKey: '',
            secretKey: ''
          },
          
          paymentSettings: {
            enablePayments: false,
            currency: 'INR',
            stripe: { enabled: false, publishableKey: '', secretKey: '', webhookSecret: '' },
            razorpay: { enabled: false, keyId: '', keySecret: '' },
            paypal: { enabled: false, clientId: '', clientSecret: '', mode: 'sandbox' }
          },
          
          databaseSettings: {
            autoBackup: true,
            backupFrequency: 'daily',
            backupTime: '02:00',
            retentionDays: 30
          },
          
          securitySettings: {
            enableCaptcha: false,
            captchaProvider: 'recaptcha',
            captchaSiteKey: '',
            captchaSecretKey: '',
            maxLoginAttempts: 5,
            lockoutDuration: 30,
            enableRateLimiting: true,
            rateLimitRequests: 100,
            rateLimitWindow: 15
          },
          
          apiSettings: {
            enableAPI: true,
            apiVersion: 'v1',
            enableDocs: true,
            enableCORS: true,
            allowedOrigins: []
          },
          
          cacheSettings: {
            enableCache: true,
            cacheProvider: 'memory',
            cacheTTL: 3600,
            redisHost: '',
            redisPort: 6379,
            redisPassword: ''
          },
          
          loggingSettings: {
            enableLogging: true,
            logLevel: 'info',
            logToFile: true,
            logToDatabase: false,
            maxLogSize: 10485760,
            logRetentionDays: 30
          },
          
          sessionSettings: {
            sessionTimeout: 30,
            sessionProvider: 'memory',
            enableRememberMe: true,
            rememberMeDuration: 30
          },
          
          performanceSettings: {
            enableCompression: true,
            enableMinification: true,
            enableLazyLoading: true,
            enableCDN: false,
            cdnUrl: ''
          },
          
          featureFlags: {
            enableChat: true,
            enableNotifications: true,
            enableReports: true,
            enableExports: true,
            enableImports: true
          }
        };
  
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(defaults));
      }
    }
  
    /**
     * Get all settings
     */
    getSettings() {
      try {
        const settings = localStorage.getItem(this.STORAGE_KEY);
        return settings ? JSON.parse(settings) : null;
      } catch (error) {
        console.error('Error getting settings:', error);
        return null;
      }
    }
  
    /**
     * Update settings
     */
    updateSettings(updates) {
      try {
        const current = this.getSettings();
        const updated = { ...current, ...updates };
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updated));
        return true;
      } catch (error) {
        console.error('Error updating settings:', error);
        return false;
      }
    }
  
    /**
     * Update nested settings
     */
    updateNestedSettings(parent, updates) {
      try {
        const current = this.getSettings();
        current[parent] = { ...current[parent], ...updates };
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(current));
        return true;
      } catch (error) {
        console.error('Error updating nested settings:', error);
        return false;
      }
    }
  
    /**
     * Toggle maintenance mode
     */
    toggleMaintenanceMode(message = null) {
      try {
        const current = this.getSettings();
        current.maintenanceMode = !current.maintenanceMode;
        if (message) {
          current.maintenanceMessage = message;
        }
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(current));
        return current.maintenanceMode;
      } catch (error) {
        console.error('Error toggling maintenance mode:', error);
        return false;
      }
    }
  
    /**
     * Add allowed origin
     */
    addAllowedOrigin(origin) {
      try {
        const current = this.getSettings();
        if (!current.apiSettings.allowedOrigins.includes(origin)) {
          current.apiSettings.allowedOrigins.push(origin);
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(current));
        }
        return true;
      } catch (error) {
        console.error('Error adding allowed origin:', error);
        return false;
      }
    }
  
    /**
     * Remove allowed origin
     */
    removeAllowedOrigin(origin) {
      try {
        const current = this.getSettings();
        current.apiSettings.allowedOrigins = current.apiSettings.allowedOrigins.filter(o => o !== origin);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(current));
        return true;
      } catch (error) {
        console.error('Error removing allowed origin:', error);
        return false;
      }
    }
  
    /**
     * Add allowed file type
     */
    addAllowedFileType(fileType) {
      try {
        const current = this.getSettings();
        if (!current.storageSettings.allowedFileTypes.includes(fileType)) {
          current.storageSettings.allowedFileTypes.push(fileType);
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(current));
        }
        return true;
      } catch (error) {
        console.error('Error adding file type:', error);
        return false;
      }
    }
  
    /**
     * Remove allowed file type
     */
    removeAllowedFileType(fileType) {
      try {
        const current = this.getSettings();
        current.storageSettings.allowedFileTypes = current.storageSettings.allowedFileTypes.filter(ft => ft !== fileType);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(current));
        return true;
      } catch (error) {
        console.error('Error removing file type:', error);
        return false;
      }
    }
  
    /**
     * Reset to defaults
     */
    resetToDefaults() {
      try {
        localStorage.removeItem(this.STORAGE_KEY);
        this.initializeDefaults();
        return true;
      } catch (error) {
        console.error('Error resetting settings:', error);
        return false;
      }
    }
  
    /**
     * Export settings
     */
    exportSettings() {
      try {
        const settings = this.getSettings();
        return JSON.stringify(settings, null, 2);
      } catch (error) {
        console.error('Error exporting settings:', error);
        return null;
      }
    }
  
    /**
     * Import settings
     */
    importSettings(jsonString) {
      try {
        const settings = JSON.parse(jsonString);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings));
        return true;
      } catch (error) {
        console.error('Error importing settings:', error);
        return false;
      }
    }
  }
  
  // Export singleton instance
  export const systemSettingsController = new SystemSettingsController();